/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {FormattedMessage, useIntl} from 'react-intl'
import {validatePassword} from '../../utils/password-utils'
import Link from '../link'
import React from 'react'
import useGlobalFields from './useGlobalFields'

export default function useRegistrationFields({form, prefix = ''}) {
    const {formatMessage} = useIntl()

    const {
        control,
        formState: {errors}
    } = form

    const {firstName, lastName, email} = useGlobalFields({
        form,
        prefix
    })

    return {
        firstName,
        lastName,
        email,
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'Password',
                id: 'use_registration_fields.label.password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please create a password.',
                    id: 'use_registration_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least 8 characters.',
                            id: 'use_registration_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one uppercase letter.',
                            id: 'use_registration_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one lowercase letter.',
                            id: 'use_registration_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one number.',
                            id: 'use_registration_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one special character.',
                            id: 'use_registration_fields.error.special_character'
                        })
                }
            },
            error: errors[`${prefix}password`],
            inputProps: {
                autoComplete: 'new-password'
            },
            control
        },
        newsletter: {
            name: `${prefix}newsletter`,
            label: formatMessage(
                {
                    defaultMessage:
                        'Yes, I would like personalized emails about exclusive content, product recommendations and promotional updates from Stokke Group (Stokke AS and its subsidiaries) based on my profile data, purchase history and activities in Stokke digital channels. Unsubscribe link in every newsletter and in MyAccount. What personal data we process, why and how: {privacy}.',
                    id: 'forms.marketing2'
                },
                {
                    br: () => <br />,
                    privacy: (
                        <Link
                            href="/customer-service/legal/privacy-policy.html"
                            isExternal
                            fontSize="inherit"
                        >
                            <FormattedMessage
                                defaultMessage="Privacy Policy"
                                id="footer.link.privacy_policy"
                            />
                        </Link>
                    )
                }
            ),
            type: 'checkbox',
            defaultValue: false,
            inputProps: {size: 'sm'},
            control
        }
    }
}
